const API_BASE_URL = `${process.env.REACT_APP_CRM_ENDPOINT_URL}/en/api/v1.0`;
const SECRET_KEY = process.env.REACT_APP_SECRETKEY;

export const kcbService = {
    /**
     * Lấy lịch sử khám bệnh theo mã KCB và mã đăng ký
     * @param {string} makcb - Mã khám bệnh
     * @param {string} mandk - Mã đăng ký
     * @returns {Promise<Object>} Dữ liệu lịch sử khám bệnh
     */
    async getLichSuKCB(makcb, mandk) {
        if (!makcb || !mandk) {
            throw new Error('Mã KCB và mã đăng ký là bắt buộc');
        }

        try {
            const response = await fetch(
                `${API_BASE_URL}/KetQuaCdha/get-ls-kcb/${SECRET_KEY}?makcb=${makcb}&mandk=${mandk}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            console.error("Lỗi fetch dữ liệu lịch sử KCB:", error);
            throw error;
        }
    },

    /**
     * Lấy kết quả khám chi tiết của một lượt khám.
     *
     * Dùng chung WareHouseDetailDto với app mobile (Warehouse/detail-v2), nên có
     * đủ chỉ số xét nghiệm, kết luận, tư vấn và thông tin phiếu — những thứ
     * get-ls-kcb không trả về.
     *
     * @param {string} makcb - Mã khám bệnh của lượt khám cần xem
     * @param {string} mandk - Mã cơ sở KCB
     * @returns {Promise<Object|null>} WareHouseDetailDto, hoặc null nếu không có
     */
    async getKetQuaKham(makcb, mandk) {
        if (!makcb || !mandk) return null;

        try {
            const response = await fetch(
                `${API_BASE_URL}/KetQuaCdha/ket-qua-kham/${SECRET_KEY}?makcb=${makcb}&mandk=${mandk}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            return result?.data ?? null;
        } catch (error) {
            // Không chặn cả trang: phần cận lâm sàng từ get-ls-kcb vẫn hiển thị được.
            console.error("Lỗi fetch kết quả khám chi tiết:", error);
            return null;
        }
    },

    /**
     * Tải phiếu trả kết quả (PDF) của một lượt khám về dạng Blob.
     *
     * Chấp nhận cả hai kiểu response vì tuỳ endpoint mà backend trả khác nhau:
     *  - binary application/pdf (endpoint cho web)
     *  - JSON { data: { base64, fileName } } (kiểu mobile đang dùng)
     * Tự dựng Blob thay vì mở thẳng url để còn đọc được thông báo lỗi từ API,
     * thay vì mở ra một tab trắng khi hồ sơ chưa có phiếu.
     *
     * @param {string} makcb - Mã khám bệnh
     * @param {string} mandk - Mã cơ sở KCB
     * @returns {Promise<{blob: Blob, fileName: string}>}
     */
    async getKetQuaPdfBlob(makcb, mandk) {
        const response = await fetch(
            `${API_BASE_URL}/KetQuaCdha/ket-qua-pdf/${SECRET_KEY}?makcb=${makcb}&mandk=${mandk}`
        );

        const contentType = response.headers.get("content-type") || "";
        const isJson = contentType.includes("application/json");

        if (!response.ok) {
            let message = "Không tải được phiếu trả kết quả.";
            if (isJson) {
                const err = await response.json().catch(() => null);
                message = err?.description || err?.Description || message;
            }
            throw new Error(message);
        }

        if (isJson) {
            const json = await response.json();
            const data = json?.data ?? json?.Data ?? {};
            const base64 = data.base64 ?? data.Base64;
            if (!base64) throw new Error("Không tải được phiếu trả kết quả.");

            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i += 1) {
                bytes[i] = binary.charCodeAt(i);
            }
            return {
                blob: new Blob([bytes], { type: "application/pdf" }),
                fileName: data.fileName ?? data.FileName ?? `KetQuaKham_${makcb}.pdf`,
            };
        }

        return {
            blob: await response.blob(),
            fileName: `KetQuaKham_${makcb}.pdf`,
        };
    },

    /**
     * Lấy thông tin chi tiết của một lượt khám
     * @param {Array} luotKhamList - Danh sách lượt khám
     * @param {string} maKcb - Mã KCB cần tìm
     * @returns {Object|null} Thông tin lượt khám hoặc null nếu không tìm thấy
     */
    getLuotKhamById(luotKhamList, maKcb) {
        return luotKhamList?.find(item => item.maKcb === maKcb) || null;
    },

    /**
     * Lọc danh sách chẩn đoán hình ảnh
     * @param {Array} canLamSangList - Danh sách cận lâm sàng
     * @returns {Array} Danh sách chẩn đoán hình ảnh
     */
    filterCdha(canLamSangList) {
        return canLamSangList?.filter(item => item.isXetNghiem === false) || [];
    },

    /**
     * Lọc danh sách xét nghiệm
     * @param {Array} canLamSangList - Danh sách cận lâm sàng
     * @returns {Array} Danh sách xét nghiệm
     */
    filterXetNghiem(canLamSangList) {
        return canLamSangList?.filter(item => item.isXetNghiem === true) || [];
    }
};
